import { useQuery } from "@tanstack/react-query";
import {
  getClubLiveConfig,
  getClubLiveStatus,
  getClubOverview,
  getTranscodeProgress,
} from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import type {
  ClubLiveConfigPayload,
  ClubLiveStatusPayload,
  ClubOverviewPayload,
  TranscodeProgressMap,
} from "@/lib/types";

/**
 * Club console TanStack Query hooks (admin-console-split).
 *
 * The club-scoped polls behind /admin: the Overview payload, the slim Live
 * page's status/config, and transcode progress for the match library. The
 * operator polls live in features/super-admin/hooks/useOperatorData.ts.
 *
 * All polls:
 *   - pause when the document is hidden (`refetchIntervalInBackground:
 *     false`) — the operator isn't watching while the tab is in the
 *     background, and the 10 s + 5 s loops would otherwise hammer the
 *     backend from every parked tab
 *   - share a single cache key per endpoint so navigation between
 *     /admin/* sub-routes doesn't refetch the same data
 *   - read the (already-injected) Bearer auth token via `apiFetch`'s
 *     normal flow — no extra header plumbing here
 *
 * No `staleTime` is set here: every admin signal is intentionally
 * non-stale (the strip should reflect the latest tick), so a manual
 * refetch on tab-focus re-runs the queryFn immediately.
 */

/** Club console Overview payload (admin-console-split): club KPIs +
 * per-team rollup + this club's slot/upload signals. The club-scoped
 * replacement for the diagnostics poll on `/admin/overview` — private
 * unscoped domain, evicted on logout. */
export function useClubOverview(options?: {
  refetchIntervalMs?: number | false;
  enabled?: boolean;
}) {
  const interval = options?.refetchIntervalMs ?? 10_000;
  return useQuery<ClubOverviewPayload>({
    queryKey: unscopedKey("club-overview"),
    queryFn: getClubOverview,
    refetchInterval: interval === false ? false : interval,
    refetchIntervalInBackground: false,
    enabled: options?.enabled ?? true,
  });
}

/** Own-club per-team live state + attributable viewer counts (club Live). */
export function useClubLiveStatus(options?: {
  refetchIntervalMs?: number | false;
  enabled?: boolean;
}) {
  const interval = options?.refetchIntervalMs ?? 10_000;
  return useQuery<ClubLiveStatusPayload>({
    queryKey: unscopedKey("club-live-status"),
    queryFn: getClubLiveStatus,
    refetchInterval: interval === false ? false : interval,
    refetchIntervalInBackground: false,
    enabled: options?.enabled ?? true,
  });
}

/** Keyless club live config (enabled / RTMP base / offline message). */
export function useClubLiveConfig(options?: { enabled?: boolean }) {
  return useQuery<ClubLiveConfigPayload>({
    queryKey: unscopedKey("club-live-config"),
    queryFn: getClubLiveConfig,
    enabled: options?.enabled ?? true,
  });
}

/** Live transcode progress — authenticated + host-club-filtered on the
 * server. Polled only when `enabled` is true (the caller gates on "any
 * slot currently transcoding" so an idle library page doesn't poll). */
export function useTranscodeProgress(options: {
  enabled: boolean;
  refetchIntervalMs?: number;
}) {
  const interval = options.refetchIntervalMs ?? 5_000;
  return useQuery<TranscodeProgressMap>({
    queryKey: unscopedKey("transcode-progress"),
    queryFn: getTranscodeProgress,
    refetchInterval: options.enabled ? interval : false,
    refetchIntervalInBackground: false,
    enabled: options.enabled,
  });
}
